import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addActiveLegal, normalizeLegalKey, getFilteredActiveLegal } from "~/redux/actions/legal";
import { ReduxState } from "~/types/redux";

interface Props {
  legalKey: string;
  className?: string;
  testId?: string;
}

/**
 * This component will render a number in a superscript tag
 * The number dynamically updates to ensure it matches the approprate legal disclaimer in the footer
 * @param {string} legalKey - key to a legal object in the legal response from the OL
 * @param {string} className - optional class name to add to the component
 * @param {string} testId - optional test id to add to the component, needed as a prop for testing multiple annotations at once
 */
const LegalAnnotation: FC<Props> = ({ legalKey, className, testId = `legal-annotation-component` }: Props) => {
  const { legal, viewedProducts } = useSelector((store: ReduxState) => store);
  const { activeLegal = [], legalMap = {} } = legal || {};
  const [stateAnnotation, setAnnotation] = useState<number | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const normalizedKey = normalizeLegalKey(legalKey);
    const filteredLegal = getFilteredActiveLegal(activeLegal, viewedProducts);

    // Check if the key already exists in activeLegal (unfiltered) to decide whether to add it
    let existsInActiveLegal = activeLegal.indexOf(normalizedKey) > -1;

    if (!existsInActiveLegal) {
      const currentLegalText = legalMap[normalizedKey]?.text;
      if (currentLegalText) {
        existsInActiveLegal = activeLegal.some((existingKey) => legalMap[existingKey]?.text === currentLegalText);
      }
    }

    if (!existsInActiveLegal) {
      dispatch(addActiveLegal([legalKey]));
      return;
    }

    // Compute the annotation number from the filtered list (excludes unseen VAS entries)
    let visibleIndex = filteredLegal.indexOf(normalizedKey);

    if (visibleIndex === -1) {
      const currentLegalText = legalMap[normalizedKey]?.text;
      if (currentLegalText) {
        visibleIndex = filteredLegal.findIndex((existingKey) => legalMap[existingKey]?.text === currentLegalText);
      }
    }

    const newAnnotation = visibleIndex > -1 ? visibleIndex + 1 : null;
    if (stateAnnotation !== newAnnotation) {
      setAnnotation(newAnnotation);
    }
  }, [legalKey, activeLegal, legalMap, viewedProducts]);

  if (stateAnnotation === null) return null;

  return (
    <sup className={className} data-testid={testId}>
      [{stateAnnotation}]
    </sup>
  );
};

export default LegalAnnotation;

LegalAnnotation.displayName = "Legal Annotation";
